import { ArrowRight, Crown, ImagePlus, RotateCcw } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { prepareItemImage } from "../lib/itemImage";
import { SectionCard, SectionLabel } from "./AppSurface";

type ItemFormData = {
  name: string;
  intervalDays: number;
  nextDueAt: string;
  notificationOffsets: string;
  memo: string;
  imageFile?: File;
  removeImage: boolean;
};

type Props = {
  initialData?: Partial<ItemFormData>;
  onSubmit: (data: ItemFormData) => void;
  submitLabel?: string;
  loading?: boolean;
  preferredHours?: number[];
  preferredHoursLoading?: boolean;
  imageEditing?: {
    iconUrl?: string;
    isPro: boolean;
  };
};

function toDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getTodayString(): string {
  return toDateString(new Date());
}

function getFutureDateString(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toDateString(date);
}

const fieldClassName =
  "w-full rounded-[14px] border border-[#e3e8f0] bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#4e81ee] focus:outline-none";

export function ItemForm({
  initialData,
  onSubmit,
  submitLabel,
  loading = false,
  preferredHours,
  preferredHoursLoading = false,
  imageEditing,
}: Props) {
  const { t } = useTranslation(["items", "common"]);
  const navigate = useNavigate();
  const [name, setName] = useState(initialData?.name ?? "");
  const [intervalDays, setIntervalDays] = useState(
    initialData?.intervalDays ?? 30,
  );
  const [nextDueAt, setNextDueAt] = useState(
    initialData?.nextDueAt ?? getFutureDateString(30),
  );
  const [offsets, setOffsets] = useState<Set<string>>(
    () =>
      new Set(
        initialData?.notificationOffsets
          ? initialData.notificationOffsets.split(",")
          : ["3", "0"],
      ),
  );
  const [memo, setMemo] = useState(initialData?.memo ?? "");
  const [imageFile, setImageFile] = useState<File>();
  const [imagePreview, setImagePreview] = useState<string>();
  const [removeImage, setRemoveImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const hasCustomImage =
    imageEditing?.iconUrl?.includes("/item-images/v1/") ?? false;

  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );

  const offsetOptions = [
    { value: "0", label: t("items:offsets.sameDay") },
    { value: "1", label: t("items:offsets.daysBefore1") },
    { value: "3", label: t("items:offsets.daysBefore3") },
    { value: "7", label: t("items:offsets.daysBefore7") },
  ];

  function toggleOffset(value: string) {
    setOffsets((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || loading) return;
    onSubmit({
      name: name.trim(),
      intervalDays,
      nextDueAt,
      notificationOffsets: [...offsets]
        .sort((a, b) => Number(b) - Number(a))
        .join(","),
      memo: memo.trim(),
      imageFile,
      removeImage,
    });
  }

  async function handleImageSelection(file: File | undefined) {
    if (!file) return;
    try {
      const prepared = await prepareItemImage(file);
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImageFile(prepared);
      setImagePreview(URL.createObjectURL(prepared));
      setRemoveImage(false);
    } catch {
      alert(t("items:form.imageInvalid"));
    } finally {
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  }

  function restoreOriginalImage() {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(undefined);
    setImagePreview(undefined);
    setRemoveImage(hasCustomImage);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-3">
      {imageEditing && (
        <div>
          <SectionLabel>{t("items:form.thumbnail")}</SectionLabel>
          <SectionCard>
            <div className="flex items-center gap-4">
              <span className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#edf3ff] text-[#285bbf]">
                {!removeImage && (imagePreview || imageEditing.iconUrl) ? (
                  <img
                    src={imagePreview ?? imageEditing.iconUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImagePlus size={28} />
                )}
              </span>
              <div className="min-w-0 flex-1">
                {imageEditing.isPro ? (
                  <>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="sr-only"
                      onChange={(event) =>
                        void handleImageSelection(event.target.files?.[0])
                      }
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="flex min-h-10 items-center gap-2 rounded-[10px] border border-[#4e81ee] px-3 text-xs font-bold text-[#285bbf]"
                    >
                      <ImagePlus size={16} />
                      {t("items:form.chooseImage")}
                    </button>
                    <p className="mt-1.5 text-[11px] text-gray-400">
                      {t("items:form.imageHint")}
                    </p>
                    {(imageFile || hasCustomImage) && !removeImage && (
                      <button
                        type="button"
                        onClick={restoreOriginalImage}
                        className="mt-1 flex min-h-9 items-center gap-1.5 text-xs font-bold text-gray-500"
                      >
                        <RotateCcw size={14} />
                        {t("items:form.restoreImage")}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-2.5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-amber-50 text-amber-600">
                        <Crown size={16} />
                      </span>
                      <div className="min-w-0 pt-0.5">
                        <p className="text-xs font-bold text-amber-700">
                          {t("items:form.imageProOnly")}
                        </p>
                        <p className="mt-0.5 text-[11px] leading-[1.55] text-gray-500">
                          {t("items:form.imageProDescription")}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/paywall")}
                      className="ml-10 inline-flex min-h-10 items-center gap-1.5 rounded-[10px] px-1 text-xs font-bold text-[#285bbf] transition-colors hover:text-[#174dbf]"
                    >
                      {t("items:form.joinProWaitlist")}
                      <ArrowRight size={14} />
                    </button>
                    {hasCustomImage && !removeImage && (
                      <button
                        type="button"
                        onClick={restoreOriginalImage}
                        className="mt-1 flex min-h-9 items-center gap-1.5 text-xs font-bold text-gray-500"
                      >
                        <RotateCcw size={14} />
                        {t("items:form.restoreImage")}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      <div>
        <SectionLabel>{t("items:detail.details")}</SectionLabel>
        <SectionCard className="space-y-5">
          <div>
            <label
              htmlFor="item-name"
              className="mb-2 block text-xs font-bold text-gray-600"
            >
              {t("items:form.name")}
            </label>
            <input
              id="item-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("items:form.namePlaceholder")}
              required
              maxLength={100}
              className={fieldClassName}
            />
          </div>

          <div>
            <label
              htmlFor="interval-days"
              className="mb-2 block text-xs font-bold text-gray-600"
            >
              {t("items:form.intervalDays")}
            </label>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-400">
                {t("items:form.minDays")}
              </span>
              <input
                id="interval-days"
                type="range"
                value={intervalDays}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setIntervalDays(value);
                  setNextDueAt(getFutureDateString(value));
                }}
                min={1}
                max={90}
                step={1}
                className="slider-blue w-full"
                style={
                  {
                    "--slider-progress": `${((intervalDays - 1) / 89) * 100}%`,
                  } as React.CSSProperties
                }
              />
              <span className="text-[11px] text-gray-400">
                {t("items:form.maxDays")}
              </span>
            </div>
            <p className="mt-2 text-center text-sm font-bold text-gray-900">
              {t("items:form.intervalDisplay", { count: intervalDays })}
            </p>
          </div>

          <div>
            <label
              htmlFor="next-due-at"
              className="mb-2 block text-xs font-bold text-gray-600"
            >
              {t("items:form.nextDueDate")}
            </label>
            <input
              id="next-due-at"
              type="date"
              value={nextDueAt}
              onChange={(event) => setNextDueAt(event.target.value)}
              min={getTodayString()}
              className={fieldClassName}
            />
          </div>
        </SectionCard>
      </div>

      <div>
        <SectionLabel>{t("items:form.notificationTiming")}</SectionLabel>
        <SectionCard className="space-y-5">
          <div className="flex flex-wrap gap-2">
            {offsetOptions.map((option) => {
              const selected = offsets.has(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOffset(option.value)}
                  className={`min-h-10 rounded-full border px-3.5 text-xs font-bold transition-colors ${
                    selected
                      ? "border-[#4e81ee] bg-[#edf3ff] text-[#285bbf]"
                      : "border-[#e3e8f0] bg-white text-gray-600 hover:bg-[#f0f3f8]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div>
            <p className="mb-2 text-xs font-bold text-gray-600">
              {t("items:form.notificationTimezone")}
            </p>
            {preferredHoursLoading ? (
              <output className="flex gap-2" aria-label={t("common:loading")}>
                {[0, 1, 2].map((value) => (
                  <span
                    key={value}
                    className="h-7 w-12 rounded-full bg-gray-100 [animation:skeleton-pulse_1.2s_ease-in-out_infinite]"
                  />
                ))}
              </output>
            ) : preferredHours?.length ? (
              <div className="flex flex-wrap gap-2">
                {preferredHours
                  .slice()
                  .sort((a, b) => a - b)
                  .map((hour) => (
                    <span
                      key={hour}
                      className="rounded-full bg-[#f0f3f8] px-3 py-1.5 text-xs font-medium text-gray-600"
                    >
                      {t("common:units.hours", { count: hour })}
                    </span>
                  ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">{t("items:form.notSet")}</p>
            )}
            <button
              type="button"
              onClick={() => navigate("/settings")}
              className="mt-2 min-h-10 text-xs font-bold text-[#285bbf]"
            >
              {t("items:form.changeInSettings")}
            </button>
          </div>
        </SectionCard>
      </div>

      <div>
        <SectionLabel>{t("items:form.memo")}</SectionLabel>
        <SectionCard>
          <textarea
            id="memo"
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            placeholder={t("items:form.memoPlaceholder")}
            rows={3}
            className={`${fieldClassName} resize-none`}
          />
        </SectionCard>
      </div>

      <div className="sticky bottom-0 -mx-1 bg-white/95 px-1 pt-2 backdrop-blur">
        <button
          type="submit"
          disabled={loading || !name.trim()}
          className="flex h-[52px] w-full items-center justify-center rounded-[14px] bg-[#4e81ee] text-sm font-bold text-white transition-colors hover:bg-[#3e70dc] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
        >
          {loading
            ? t("items:form.submitting")
            : (submitLabel ?? t("items:form.submit"))}
        </button>
      </div>
    </form>
  );
}
